import React, { useState } from 'react'
import { useAppContext } from '@agtech/core/app/AgTechAppContext';
import { agtechDelay } from '@agtech/core/utilities/AgTechUtilities';
import { AgTechMessage } from '@agtech/core/functions/AgTechAppMessaging';
import { useModalContext } from '@agtech/web/components/Portals/Modals/AgTechModal';
import { AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import AgTechWarningMessage from '@agtech/web/components/Messaging/AgTechWarningMessage';
import { AgTechLoader } from '@agtech/web/components/Pages/Loading/AgTechLoadingSurface';
import { AgTechDataActionExecutionResult } from '@agtech/core/data/actions/AgTechDataActions';
import { AgTechImportResult, AgTechImportTerminologyProps, AgTechImportExecutionProps, AgTechImportColumn, AgTechImportItem, AgTechImportItemsProps, AgTechImportData } from './common/AgTechImportTypes';
import AgTechConflictingImportResultsView from './views/AgTechConflictingImportResultsView';
import AgTechFailedImportResultsView from './views/AgTechFailedImportResultsView';
import AgTechSuccessfulImportResultsView from './views/AgTechSuccessfulImportResultsView';
import {read, utils } from 'xlsx'
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';

export enum AgTechImportStatus {
    Open,
    Importing,
    ImportFailed,
    ImportHadConflicts,
    ShowingFailures,
    ShowingConflicts,
    ShowingResults,
    Loading,
    Submitting
}

export type AgTechImportState<TItem> = {
    status: AgTechImportStatus,
    result?: AgTechImportResult<TItem>
}

export type AgTechImportModalBodyProps = {
    handleFileUploaded: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
}

export type AgTechImportModalProps<TItem, TContext = {}> = AgTechImportExecutionProps<TItem, TContext> & {
    structure: {
        title: string,
        body: (props: AgTechImportModalBodyProps) => JSX.Element,
        importResultsModalWidth?: string
    },
    content: AgTechImportTerminologyProps,
    behavior: {
        onImportSubmitted: (importedItems: TItem[]) => Promise<AgTechDataActionExecutionResult<any>>
    }
}

const AgTechImportModal = <TItem, TContext = {}>(props: AgTechImportModalProps<TItem, TContext>) => {
    return (
        <AgTechQueryLoadingSurface query={props.items.existingItemsQuery}>
            {(data) => (
                <AgTechImportModalBody {...props} data={{
                    items: data.items,
                    context: data.context ?? {} as any
                }} />   
            )}
        </AgTechQueryLoadingSurface>
    )
}

const AgTechImportModalBody = <TItem, TContext>(
    props: AgTechImportModalProps<TItem, TContext> & { data: AgTechImportData<TItem, TContext> }
) => {
    const appContext = useAppContext();
    const modalContext = useModalContext();
    
    const [importState, updateImportState] = useState<AgTechImportState<TItem>>({
        status: AgTechImportStatus.Open
    });

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const importStatePriorToUpload = importState.status;

        updateImportState({
            status: AgTechImportStatus.Importing
        });

        if (e.currentTarget && e.currentTarget.files) {
            const uploadedFileBuffer = await e.currentTarget.files[0].arrayBuffer();
            const uploadedFileContent = read(uploadedFileBuffer);
            const uploadedFileSheet = uploadedFileContent.Sheets[uploadedFileContent.SheetNames[0]];

            const uploadedRows = utils.sheet_to_json<string[]>(uploadedFileSheet, {
                header: 1
            });

            const columnHeader = uploadedRows[0];
            const rows = uploadedRows.slice(1);

            const validationResult = await validateAgTechImport<TItem, TContext>({
                ...props,
                header: columnHeader,
                rows: rows
            });

            if (validationResult) {
                appContext.messaging.showWarning(validationResult);

                updateImportState({
                    ...importState,
                    status: importStatePriorToUpload
                });

                return;
            }

            const importResult = await executeAgTechImport<TItem, TContext>({
                ...props,
                header: columnHeader,
                rows: rows
            }, props.data);

            if (!importResult.wasSuccessful) {
                updateImportState({
                    status: AgTechImportStatus.ImportFailed,
                    result: importResult
                });
            }
            else if (importResult.hadConflicts) {
                updateImportState({
                    status: AgTechImportStatus.ImportHadConflicts,
                    result: importResult
                });
            }
            else {
                await showImportResults(importResult);
            }
            
        }
    }

    const showImportFailures = async (importResult?: AgTechImportResult<TItem>) => {
        updateImportState({
            status: AgTechImportStatus.Loading
        });

        await modalContext.growModal('720px', props.structure.importResultsModalWidth ?? '1200px');

        updateImportState({
            status: AgTechImportStatus.ShowingFailures,
            result: importResult
        });
    }

    const showImportConflicts = async (importResult?: AgTechImportResult<TItem>) => {
        updateImportState({
            status: AgTechImportStatus.Loading
        });

        await modalContext.growModal('720px', props.structure.importResultsModalWidth ?? '1200px');

        updateImportState({
            status: AgTechImportStatus.ShowingConflicts,
            result: importResult
        });
    }

    const showImportResults = async (importResult?: AgTechImportResult<TItem>) => {
        await modalContext.growModal('720px', props.structure.importResultsModalWidth ?? '1200px');

        updateImportState({
            status: AgTechImportStatus.ShowingResults,
            result: importResult
        });
    }

    const submitResult = async (importedItems: TItem[]) => {
        updateImportState({
            status: AgTechImportStatus.Loading
        });

        let statePriorToSubmission = { ...importState };

        updateImportState({
            status: AgTechImportStatus.Submitting
        });

        await agtechDelay(1000);

        let didSubmissionSucceed = false;

        try
        {
            let importResult = await props.behavior.onImportSubmitted(importedItems);
            didSubmissionSucceed = importResult.success !== undefined;
        }
        catch
        {
            didSubmissionSucceed = false;
        }

        if (didSubmissionSucceed) {
            modalContext.closeModal();
        }
        else {
            updateImportState({
                ...statePriorToSubmission
            });
        }
    }
    
    return (
        <>
            {importState.status === AgTechImportStatus.Open ? (
                <div className='stretched row-based container'>
                    {props.structure.body({
                        handleFileUploaded: handleFileUpload
                    })}
                </div>
            ) : importState.status === AgTechImportStatus.Importing ? (
                <div className='centered flexed fade-in-400 tall container'>
                    <AgTechLoader text={`Importing ${props.content.importEntityPlural.toLowerCase()}...`} />
                </div>
            ) : importState.status === AgTechImportStatus.ImportFailed ? (
                <div className='centered flexed fade-in-400 tall container'>
                    <AgTechWarningMessage
                        header={`One or more ${props.content.importEntityPlural.toLowerCase()} in the selected file could not be imported`}
                        headerFontSize='font-size-larger'
                        details={`To view these failed ${props.content.importEntityPlural.toLowerCase()} and import again, please click the button below`}
                        actionButton={() => (
                            <AgTechButton
                                text={`View failed ${props.content.importEntitySingular.toLowerCase()} imports`}
                                icon='fas fa-eye'
                                action={async () => showImportFailures(importState.result)}
                                classes='button-highlight py-3 font-size-above-standard rounded'
                            />
                        )}
                    />
                </div>
            ) : importState.status === AgTechImportStatus.ImportHadConflicts ? (
                <div className='centered flexed fade-in-400 tall container'>
                    <AgTechWarningMessage
                        header={`One or more ${props.content.importEntityPlural.toLowerCase()} in the selected file already exist in the system`}
                        headerFontSize='font-size-larger'
                        details={`Please look over these existing ${props.content.importEntityPlural.toLowerCase()} before finishing the import`}
                        actionButton={() => (
                            <AgTechButton
                                text={`View import results`}
                                icon='fas fa-eye'
                                action={async () => showImportConflicts(importState.result)}
                                classes='button-highlight py-3 font-size-above-standard rounded'
                            />
                        )}
                    />
                </div>
            ) : importState.status === AgTechImportStatus.ShowingFailures ? (
                <div className='centered flexed fade-in-400 tall container'>
                    {importState.result ? (
                        <AgTechFailedImportResultsView
                            importProps={props}
                            importData={props.data}
                            content={props.content}
                            importResult={importState.result}
                            onImportAgain={handleFileUpload}
                        />
                    ) : null}
                </div>
            ) : importState.status === AgTechImportStatus.ShowingConflicts ? (
                <div className='centered flexed fade-in-400 tall container'>
                    {importState.result ? (
                        <AgTechConflictingImportResultsView
                            importProps={props}
                            importData={props.data}
                            importResult={importState.result}
                            content={props.content}
                            onImportAgain={handleFileUpload}
                            onImportSubmitted={submitResult}
                        />
                    ) : null}
                </div>
            ) : importState.status === AgTechImportStatus.ShowingResults ? (
                <div className='centered flexed fade-in-400 tall container'>
                    {importState.result ? (
                        <AgTechSuccessfulImportResultsView
                            importProps={props}
                            importResult={importState.result}
                            content={props.content}
                            onImportAgain={handleFileUpload}
                            onImportSubmitted={submitResult}
                            importData={props.data}
                        />
                    ) : null}
                </div>
            ) : importState.status === AgTechImportStatus.Submitting ? (
                <AgTechLoader text={`Submitting ${props.content.importEntityPlural.toLowerCase()}...`} />
            ) : null}
        </>
    )
}

type AgTechImportInternalExecutionProps<TItem, TContext = {}> = AgTechImportExecutionProps<TItem, TContext> & {
    header: string[]
    rows: string[][]
}

type AgTechImportColumnIndex<TItem, TContext> = {
    [index: number]: AgTechImportColumn<TItem, TContext>
}

const validateAgTechImport = async <TItem, TContext = {}>(
    props: AgTechImportInternalExecutionProps<TItem, TContext>
): Promise<AgTechMessage | void> => {
    let missingColumns: string[] = [];

    props.columns.forEach(column => {
        let matchingColumnIndexNumber = props.header.indexOf(column.columnName);

        if (matchingColumnIndexNumber < 0) {
            missingColumns.push(column.columnName);
        }
    });

    if (missingColumns.length > 0) {
        return {
            header: 'The following columns could not be found in the imported file:',
            details: missingColumns.join(', ')
        }
    }
}

const executeAgTechImport = async <TItem, TContext = {}>(
    props: AgTechImportInternalExecutionProps<TItem, TContext>,
    data: AgTechImportData<TItem, TContext>
): Promise<AgTechImportResult<TItem>> => {
    const importResult: AgTechImportResult<TItem> = {
        items: [],
        wasSuccessful: true,
        hadConflicts: false
    };

    const columnIndex: AgTechImportColumnIndex<TItem, TContext> = {};

    props.columns.forEach(column => {
        const matchingColumnIndexNumber = props.header.indexOf(column.columnName);

        if (matchingColumnIndexNumber >= 0) {
            columnIndex[matchingColumnIndexNumber] = {...column};
        }
        else {
            importResult.wasSuccessful = false;
        }
    });

    const existingItemIndex: { [key: string]: { existingItem: TItem, warning: string } } = {};

    const existingItems = props.items.existingItemsQuery.queryResult.data ?? [];

    existingItems.items.forEach(existingItem => {
        props.items.itemKeys.forEach(itemKey => {
            const existingItemKey = itemKey(existingItem);

            existingItemIndex[existingItemKey.key] = {
                existingItem,
                warning: existingItemKey.warning
            };
        });
    });

    const existingItemKeys = Object.keys(existingItemIndex);

    for (let i = 0; i < props.rows.length; i++) {
        const importItem: AgTechImportItem<TItem> = {
            itemRow: i+1,
            importedItem: {
                ...props.items.defaultItem
            },
            isConflictingItem: false,
            isValidItem: true,
            invalidColumns: {},
        };

        for (let j = 0; j < props.rows[i].length; j++) {
            const correspondingColumn = columnIndex[j];

            if (correspondingColumn) {
                const cellValue = props.rows[i][j];

                try {
                    let validationResult = correspondingColumn.validateItemValue ?
                        correspondingColumn.validateItemValue(cellValue, [], data.context as any) :
                        { isValid: true };

                    if (validationResult.isValid) {
                        correspondingColumn.setItemValue(importItem.importedItem, cellValue, data.context as any);
                    }
                    else {
                        importItem.isValidItem = false;
                        importItem.invalidColumns[correspondingColumn.columnName] = {
                            importedValue: cellValue,
                            errorMessage: validationResult.errorMessage ?? 'Unable to import'
                        };

                        importResult.wasSuccessful = false;
                    }
                }
                catch {
                    importItem.isValidItem = false;
                    importItem.invalidColumns[correspondingColumn.columnName] = {
                        importedValue: cellValue,
                        errorMessage: 'Unable to import'
                    };

                    importResult.wasSuccessful = false;
                }
            }
        }

        if (importItem.isValidItem) {
            props.items.itemKeys.forEach(itemKey => {
                let importedItemKey = itemKey(importItem.importedItem).key;

                if (existingItemKeys.find(existingItemKey => existingItemKey === importedItemKey)) {
                    let matchingItem = existingItemIndex[importedItemKey].existingItem;
    
                    if (matchingItem) {
                        importItem.importedItem = { ...matchingItem };
                        importItem.isConflictingItem = true;
                        importItem.importFailureMessage = existingItemIndex[importedItemKey].warning;

                        importResult.hadConflicts = true;
                    }
                }
            });
        }

        importResult.items.push({
            ...importItem
        });
    }

    return importResult;
}

export default AgTechImportModal